import { useState, useEffect } from "react";
import { Lock, Settings, Image, FileText, MapPin, Send, Plus, Trash2, Save, Loader2, Wand2, Eye, EyeOff, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { WAVE_1_CITIES, WAVE_2_CITIES, WAVE_3_CITIES } from "@/data/cityWaves";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPage() {
  const [token, setToken] = useState(localStorage.getItem("admin_token") || "");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="admin-login">
        <Card className="w-full max-w-sm shadow-lg">
          <CardHeader className="text-center">
            <div className="w-12 h-12 mx-auto mb-2 rounded-xl bg-primary flex items-center justify-center">
              <Lock className="w-6 h-6 text-white" />
            </div>
            <CardTitle className="text-xl">Admin Login</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <input type="text" placeholder="Username" value={username} onChange={e => setUsername(e.target.value)}
              className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/10"
              data-testid="admin-username" />
            <input type="password" placeholder="Password" value={password} onChange={e => setPassword(e.target.value)}
              className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/10"
              data-testid="admin-password" />
            <Button className="w-full bg-primary hover:bg-primary/90" disabled={loginLoading}
              data-testid="admin-login-button"
              onClick={async () => {
                setLoginLoading(true);
                try {
                  const res = await axios.post(`${API}/admin/login`, { username, password });
                  setToken(res.data.token);
                  localStorage.setItem("admin_token", res.data.token);
                  toast.success("Logged in successfully");
                } catch { toast.error("Invalid credentials"); }
                setLoginLoading(false);
              }}>
              {loginLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Login"}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const headers = { Authorization: `Bearer ${token}` };
  const handleLogout = () => { setToken(""); localStorage.removeItem("admin_token"); };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      <div className="bg-white border-b border-slate-200 py-3">
        <div className="container-main flex items-center justify-between">
          <h1 className="text-lg font-bold text-slate-900">AffordScope Admin</h1>
          <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="admin-logout">Logout</Button>
        </div>
      </div>

      <div className="container-main py-6">
        <Tabs defaultValue="settings">
          <TabsList className="mb-6 bg-white border border-slate-200">
            <TabsTrigger value="settings" data-testid="tab-settings"><Settings className="w-4 h-4 mr-1" /> Settings</TabsTrigger>
            <TabsTrigger value="banners" data-testid="tab-banners"><Image className="w-4 h-4 mr-1" /> Banners</TabsTrigger>
            <TabsTrigger value="blog" data-testid="tab-blog"><FileText className="w-4 h-4 mr-1" /> Blog</TabsTrigger>
            <TabsTrigger value="cities" data-testid="tab-cities"><MapPin className="w-4 h-4 mr-1" /> Cities & Scale</TabsTrigger>
            <TabsTrigger value="indexnow" data-testid="tab-indexnow"><Send className="w-4 h-4 mr-1" /> IndexNow</TabsTrigger>
          </TabsList>

          <TabsContent value="settings"><SettingsTab headers={headers} /></TabsContent>
          <TabsContent value="banners"><BannersTab headers={headers} /></TabsContent>
          <TabsContent value="blog"><BlogTab headers={headers} /></TabsContent>
          <TabsContent value="cities"><CitiesTab headers={headers} /></TabsContent>
          <TabsContent value="indexnow"><IndexNowTab headers={headers} /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function SettingsTab({ headers }) {
  const [settings, setSettings] = useState({ site_name: "AffordScope", contact_email: "support@lifeownit.com", adsense_id: "ca-pub-9506351355668775", ga4_id: "G-XXXXXXXXXX" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/settings`, { headers }).then(r => setSettings(prev => ({ ...prev, ...r.data }))).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try { await axios.put(`${API}/admin/settings`, settings, { headers }); toast.success("Settings saved"); }
    catch { toast.error("Failed to save"); }
    setSaving(false);
  };

  return (
    <Card data-testid="settings-panel">
      <CardHeader><CardTitle>Site Settings</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        {Object.entries(settings).filter(([k]) => k !== 'type' && k !== '_id').map(([key, val]) => (
          <div key={key}>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1 block">{key.replace(/_/g, ' ')}</label>
            <input value={val || ''} onChange={e => setSettings(p => ({ ...p, [key]: e.target.value }))}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm outline-none focus:border-primary" />
          </div>
        ))}
        <Button onClick={save} disabled={saving} className="bg-primary hover:bg-primary/90" data-testid="save-settings">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />} Save Settings
        </Button>
      </CardContent>
    </Card>
  );
}

function BannersTab({ headers }) {
  const [banners, setBanners] = useState([]);
  const [form, setForm] = useState({ name: "", html_content: "", placement: "sidebar", page_type: "all", active: true });

  useEffect(() => { loadBanners(); }, []);
  const loadBanners = () => axios.get(`${API}/admin/banners`, { headers }).then(r => setBanners(r.data)).catch(() => {});

  const create = async () => {
    try { await axios.post(`${API}/admin/banners`, form, { headers }); loadBanners(); setForm({ name: "", html_content: "", placement: "sidebar", page_type: "all", active: true }); toast.success("Banner created"); }
    catch { toast.error("Failed to create banner"); }
  };

  const del = async (id) => {
    try { await axios.delete(`${API}/admin/banners/${id}`, { headers }); loadBanners(); toast.success("Deleted"); }
    catch { toast.error("Failed to delete"); }
  };

  return (
    <div className="space-y-6" data-testid="banners-panel">
      <Card>
        <CardHeader><CardTitle>Add Banner</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <input placeholder="Banner Name" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="banner-name" />
          <textarea placeholder="HTML Content (ad code, affiliate link, etc.)" value={form.html_content}
            onChange={e => setForm(p => ({ ...p, html_content: e.target.value }))} rows={4}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono" data-testid="banner-html" />
          <div className="flex gap-3">
            <select value={form.placement} onChange={e => setForm(p => ({ ...p, placement: e.target.value }))}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="banner-placement">
              <option value="sidebar">Sidebar</option>
              <option value="bottom">Bottom</option>
              <option value="header">Header</option>
            </select>
            <select value={form.page_type} onChange={e => setForm(p => ({ ...p, page_type: e.target.value }))}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm">
              <option value="all">All Pages</option>
              <option value="city">City Pages</option>
              <option value="salary">Salary Pages</option>
              <option value="home">Homepage</option>
            </select>
          </div>
          <Button onClick={create} className="bg-primary hover:bg-primary/90" data-testid="create-banner">
            <Plus className="w-4 h-4 mr-2" /> Add Banner
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Active Banners ({banners.length})</CardTitle></CardHeader>
        <CardContent>
          {banners.length === 0 ? <p className="text-sm text-slate-400">No banners yet</p> :
            <div className="space-y-3">
              {banners.map(b => (
                <div key={b.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div>
                    <p className="text-sm font-medium text-slate-900">{b.name}</p>
                    <p className="text-xs text-slate-500">{b.placement} | {b.page_type}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={b.active ? "default" : "secondary"} className={b.active ? "bg-emerald-100 text-emerald-700" : ""}>{b.active ? "Active" : "Inactive"}</Badge>
                    <Button variant="ghost" size="sm" onClick={() => del(b.id)} data-testid={`delete-banner-${b.id}`}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          }
        </CardContent>
      </Card>
    </div>
  );
}

function BlogTab({ headers }) {
  const [blogs, setBlogs] = useState([]);
  const [form, setForm] = useState({ title: "", content: "", slug: "", meta_description: "", published: false });
  const [genTopic, setGenTopic] = useState("");
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadBlogs(); }, []);
  const loadBlogs = () => axios.get(`${API}/admin/blogs`, { headers }).then(r => setBlogs(r.data)).catch(() => {});

  const generate = async () => {
    if (!genTopic) return;
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/admin/blogs/generate`, { topic: genTopic }, { headers });
      setForm({ title: res.data.title, content: res.data.content, slug: res.data.slug, meta_description: res.data.meta_description, published: false });
      toast.success("Blog generated! Review and publish.");
    } catch { toast.error("Generation failed"); }
    setGenerating(false);
  };

  const saveBlog = async () => {
    if (!form.title || !form.content || !form.slug) { toast.error("Title, content, and slug required"); return; }
    setSaving(true);
    try { await axios.post(`${API}/admin/blogs`, form, { headers }); loadBlogs(); setForm({ title: "", content: "", slug: "", meta_description: "", published: false }); toast.success("Blog saved"); }
    catch { toast.error("Failed to save"); }
    setSaving(false);
  };

  const togglePublish = async (blog) => {
    try {
      await axios.put(`${API}/admin/blogs/${blog.id}`, { published: !blog.published }, { headers });
      loadBlogs(); toast.success(blog.published ? "Unpublished" : "Published");
    } catch { toast.error("Failed to update"); }
  };

  const deleteBlog = async (id) => {
    try { await axios.delete(`${API}/admin/blogs/${id}`, { headers }); loadBlogs(); toast.success("Deleted"); }
    catch { toast.error("Failed to delete"); }
  };

  return (
    <div className="space-y-6" data-testid="blog-panel">
      <Card>
        <CardHeader><CardTitle>AI Blog Generator</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-2">
            <input placeholder="Topic (e.g., 'cost of living in Denver 2025')" value={genTopic} onChange={e => setGenTopic(e.target.value)}
              className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="blog-gen-topic" />
            <Button onClick={generate} disabled={generating || !genTopic} className="bg-primary hover:bg-primary/90" data-testid="blog-gen-button">
              {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Write / Edit Blog Post</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <input placeholder="Title" value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="blog-title" />
          <input placeholder="Slug (url-friendly)" value={form.slug} onChange={e => setForm(p => ({ ...p, slug: e.target.value }))}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="blog-slug" />
          <input placeholder="Meta Description" value={form.meta_description} onChange={e => setForm(p => ({ ...p, meta_description: e.target.value }))}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="blog-meta" />
          <textarea placeholder="Content (Markdown supported)" value={form.content}
            onChange={e => setForm(p => ({ ...p, content: e.target.value }))} rows={10}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono" data-testid="blog-content" />
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.published} onChange={e => setForm(p => ({ ...p, published: e.target.checked }))} />
              Publish immediately
            </label>
            <Button onClick={saveBlog} disabled={saving} className="bg-primary hover:bg-primary/90" data-testid="save-blog">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />} Save
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>All Posts ({blogs.length})</CardTitle></CardHeader>
        <CardContent>
          {blogs.length === 0 ? <p className="text-sm text-slate-400">No blog posts yet</p> :
            <div className="space-y-2">
              {blogs.map(b => (
                <div key={b.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div>
                    <p className="text-sm font-medium text-slate-900">{b.title}</p>
                    <p className="text-xs text-slate-500">/{b.slug} | {b.created_at ? new Date(b.created_at).toLocaleDateString() : ''}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={() => togglePublish(b)} data-testid={`toggle-blog-${b.id}`}>
                      {b.published ? <Eye className="w-4 h-4 text-emerald-600" /> : <EyeOff className="w-4 h-4 text-slate-400" />}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => deleteBlog(b.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                  </div>
                </div>
              ))}
            </div>
          }
        </CardContent>
      </Card>
    </div>
  );
}

function CitiesTab({ headers }) {
  const [cities, setCities] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [waveLoading, setWaveLoading] = useState(false);

  useEffect(() => { loadCities(); }, []);
  const loadCities = () => axios.get(`${API}/admin/cities`, { headers }).then(r => setCities(r.data)).catch(() => {});

  const uploadCSV = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await axios.post(`${API}/admin/cities/upload`, formData, { headers: { ...headers, "Content-Type": "multipart/form-data" } });
      toast.success(`Processed ${res.data.cities_processed} cities`);
      loadCities();
    } catch { toast.error("Upload failed"); }
    setUploading(false);
  };

  const deployWave = async (wave) => {
    setWaveLoading(true);
    try {
      const res = await axios.post(`${API}/admin/cities/wave`, { wave }, { headers });
      toast.success(`Wave ${wave} deployed! ${res.data.total_active} cities active.`);
      loadCities();
    } catch { toast.error("Wave deployment failed"); }
    setWaveLoading(false);
  };

  return (
    <div className="space-y-6" data-testid="cities-panel">
      <Card>
        <CardHeader><CardTitle>Wave Deployment</CardTitle></CardHeader>
        <CardContent>
          <p className="text-sm text-slate-500 mb-4">Activate cities in waves. Each wave includes all previous wave cities.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {[
              { wave: 1, label: "Wave 1", count: WAVE_1_CITIES.length, desc: "Top 20 Cities" },
              { wave: 2, label: "Wave 2", count: WAVE_2_CITIES.length, desc: "Top 50 Cities" },
              { wave: 3, label: "Wave 3", count: WAVE_3_CITIES.length, desc: "Top 100 Cities" },
            ].map(w => (
              <Card key={w.wave} className="border-slate-200">
                <CardContent className="p-4 text-center">
                  <h3 className="font-semibold text-slate-900">{w.label}</h3>
                  <p className="text-2xl font-bold font-mono text-primary my-2">{w.count}</p>
                  <p className="text-xs text-slate-500 mb-3">{w.desc}</p>
                  <Button size="sm" onClick={() => deployWave(w.wave)} disabled={waveLoading}
                    className="w-full bg-primary hover:bg-primary/90" data-testid={`deploy-wave-${w.wave}`}>
                    {waveLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : `Deploy Wave ${w.wave}`}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>CSV Upload</CardTitle></CardHeader>
        <CardContent>
          <p className="text-sm text-slate-500 mb-3">Upload a CSV to add or update cities. Required columns: name, state, state_code, slug, rent_1br, rent_2br, home_price, groceries, transportation, utilities, healthcare, state_tax_rate, col_index</p>
          <label className="inline-flex items-center gap-2 px-4 py-2 border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-50 text-sm" data-testid="csv-upload">
            <Upload className="w-4 h-4" />
            {uploading ? "Uploading..." : "Choose CSV File"}
            <input type="file" accept=".csv" onChange={uploadCSV} className="hidden" />
          </label>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Active Cities ({cities.filter(c => c.active).length} of {cities.length})</CardTitle></CardHeader>
        <CardContent>
          <div className="max-h-[400px] overflow-y-auto">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {cities.map(c => (
                <div key={c.slug} className={`text-xs p-2 rounded-lg border ${c.active ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-slate-50 border-slate-200 text-slate-500'}`}>
                  {c.name}, {c.state_code}
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function IndexNowTab({ headers }) {
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [pageCount, setPageCount] = useState(0);

  useEffect(() => {
    // Auto-count total pages
    axios.get(`${API}/admin/cities`, { headers }).then(res => {
      const cities = res.data.filter(c => c.active);
      const salaryLevels = [40, 50, 60, 70, 80, 90, 100, 120, 150, 200];
      const affordSalaries = [40, 50, 60, 70, 80, 90, 100, 120, 150];
      const commonRents = [600, 800, 1000, 1200, 1500, 1800, 2000, 2500, 3000, 3500];
      let count = 5; // homepage, blog, compare, privacy, terms
      cities.forEach(c => {
        count += 1; // city page
        count += salaryLevels.length; // salary pages
        commonRents.forEach(r => {
          if (r <= (c.rent_1br || 1500) * 1.5) count += affordSalaries.length;
        });
      });
      setPageCount(count);
    }).catch(() => {});
  }, []);

  const submit = async () => {
    setSubmitting(true);
    try {
      const res = await axios.get(`${API}/admin/cities`, { headers });
      const cities = res.data.filter(c => c.active);
      const salaryLevels = [40, 50, 60, 70, 80, 90, 100, 120, 150, 200];
      const affordSalaries = [40, 50, 60, 70, 80, 90, 100, 120, 150];
      const commonRents = [600, 800, 1000, 1200, 1500, 1800, 2000, 2500, 3000, 3500];
      const allUrls = [
        "https://affordscope.com", "https://affordscope.com/blog", "https://affordscope.com/compare",
        "https://affordscope.com/privacy", "https://affordscope.com/terms", "https://affordscope.com/disclaimer",
        "https://affordscope.com/search-city", "https://affordscope.com/budget-planner"
      ];
      cities.forEach(c => {
        allUrls.push(`https://affordscope.com/cost-of-living-in-${c.slug}`);
        salaryLevels.forEach(s => allUrls.push(`https://affordscope.com/${s}k-salary-in-${c.slug}`));
        commonRents.forEach(r => {
          if (r <= (c.rent_1br || 1500) * 1.5) {
            affordSalaries.forEach(s => allUrls.push(`https://affordscope.com/can-i-afford-${r}-rent-on-${s}k-in-${c.slug}`));
          }
        });
      });

      // Submit in batches of 500
      let totalSubmitted = 0;
      for (let i = 0; i < allUrls.length; i += 500) {
        const batch = allUrls.slice(i, i + 500);
        const r = await axios.post(`${API}/admin/indexnow`, { urls: batch }, { headers });
        totalSubmitted += r.data.submitted || batch.length;
      }
      setResult({ success: true, submitted: totalSubmitted });
      toast.success(`Submitted ${totalSubmitted} pages to Bing, Yahoo, Yandex & DuckDuckGo`);
    } catch { toast.error("Submission failed"); }
    setSubmitting(false);
  };

  return (
    <div className="space-y-6" data-testid="indexnow-panel">
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
              <Send className="w-5 h-5 text-emerald-700" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">IndexNow - Search Engine Submission</h2>
            </div>
          </div>
          <p className="text-sm text-slate-500 mb-6">
            IndexNow instantly notifies <strong>Bing, Yahoo, Yandex, and DuckDuckGo</strong> about your pages so they get indexed faster. Google uses its own crawling system (submit your sitemap via <a href="https://search.google.com/search-console" target="_blank" rel="noopener" className="text-emerald-600 hover:underline">Google Search Console</a>).
          </p>

          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-slate-50 rounded-xl p-5 text-center">
              <p className="text-3xl font-bold font-mono text-emerald-700">{pageCount.toLocaleString()}</p>
              <p className="text-xs text-slate-500 mt-1">Total Pages to Submit</p>
            </div>
            <div className="bg-slate-50 rounded-xl p-5 text-center">
              <p className="text-sm font-mono text-slate-600 break-all">affordscope-indexnow-key</p>
              <p className="text-xs text-slate-500 mt-1">IndexNow Key</p>
            </div>
          </div>

          <div className="bg-slate-50 rounded-xl p-4 mb-6">
            <p className="text-sm font-semibold text-slate-700 mb-2">What gets submitted:</p>
            <ul className="text-sm text-slate-500 space-y-1">
              <li>Homepage + Compare + Search City + Budget Planner</li>
              <li>All {pageCount > 200 ? '24' : '20'}+ city cost-of-living pages</li>
              <li>All salary-in-city pages (10 salary levels per city)</li>
              <li>All can-i-afford pages (rent + salary combinations)</li>
              <li>Blog listing + all published blog posts</li>
              <li>Legal pages (Privacy, Terms, Disclaimer)</li>
            </ul>
          </div>

          <Button onClick={submit} disabled={submitting} size="lg"
            className="w-full bg-emerald-700 hover:bg-emerald-800 text-base py-6 shadow-lg" data-testid="submit-indexnow">
            {submitting ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Send className="w-5 h-5 mr-2" />}
            Submit All Pages to Bing, Yahoo, Yandex & DuckDuckGo
          </Button>

          {result && (
            <div className="mt-4 bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-center">
              <p className="text-sm font-semibold text-emerald-800">Submitted {result.submitted.toLocaleString()} pages successfully!</p>
            </div>
          )}

          <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-4">
            <p className="text-xs text-amber-700">
              <strong>Note:</strong> For Google, you need to manually submit your sitemap at{' '}
              <a href="https://search.google.com/search-console" target="_blank" rel="noopener" className="underline">Google Search Console</a>.
              Your sitemap URL is: <code className="bg-amber-100 px-1 py-0.5 rounded text-xs">https://affordscope.com/api/sitemap.xml</code>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
